import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, RefreshCw, Sparkles, Check } from 'lucide-react';

// Each option awards a tier vote: 'beginner' | 'intermediate' | 'advanced'
const QUESTIONS = [
  {
    q: "Have you ever placed a real forex trade?",
    icon: "📊",
    options: [
      { label: "Never — I'm brand new",                vote: 'beginner' },
      { label: "A few — I'm still figuring it out",    vote: 'beginner' },
      { label: "I trade regularly with a strategy",    vote: 'intermediate' },
      { label: "I trade daily and want full automation", vote: 'advanced' },
    ],
  },
  {
    q: "How comfortable are you reading a candlestick chart?",
    icon: "🕯️",
    options: [
      { label: "What's a candlestick?",                vote: 'beginner' },
      { label: "I know the basics but get confused",   vote: 'beginner' },
      { label: "I read charts well — multi-timeframe", vote: 'intermediate' },
      { label: "I build my own systems & indicators",  vote: 'advanced' },
    ],
  },
  {
    q: "What's your trading goal right now?",
    icon: "🎯",
    options: [
      { label: "Learn the fundamentals properly",        vote: 'beginner' },
      { label: "Build a structured day-trading system",  vote: 'intermediate' },
      { label: "Add scalping to my day trading",         vote: 'advanced' },
      { label: "Run a full automated EA suite",          vote: 'advanced' },
    ],
  },
  {
    q: "How much time can you spend on trading each day?",
    icon: "⏱️",
    options: [
      { label: "15–30 minutes — just learning",   vote: 'beginner' },
      { label: "1–2 hours actively at the chart", vote: 'intermediate' },
      { label: "3+ hours — this is my focus",     vote: 'advanced' },
      { label: "Almost zero — I want it automated", vote: 'advanced' },
    ],
  },
  {
    q: "What's your investment comfort for trading education?",
    icon: "💎",
    options: [
      { label: "Under $200 — start small",          vote: 'beginner' },
      { label: "$200–$500 — serious about it",      vote: 'intermediate' },
      { label: "$500–$1,000 — full system",         vote: 'advanced' },
      { label: "Whatever it takes for the best",    vote: 'advanced' },
    ],
  },
];

const RESULTS = {
  beginner: {
    accent: 'emerald',
    badge: 'Beginner — Level 1',
    title: 'Start with the Foundations',
    blurb: "Perfect — you'll get the most value building a rock-solid base first. Forex Made Simple covers everything: chart reading, candlesticks, indicators, fundamentals, and how to place your first confident trade.",
    courses: ['Forex Made Simple — $97'],
    href: '/courses',
    cta: 'View Beginner Courses',
  },
  intermediate: {
    accent: 'amber',
    badge: 'Intermediate — Level 2',
    title: 'Master day trading + scalping',
    blurb: "You're past the basics and ready for a real edge. The 9-Step Pro System gives you a fully-automated multi-timeframe day-trading framework, and the Bow-Tie Scalping System adds a complete scalping methodology — both included at the Intermediate level.",
    courses: ['9-Step Pro System — $497 (was $697)', 'Bow-Tie Scalping System — $297'],
    href: '/intermediate',
    cta: 'View Intermediate Courses',
  },
  advanced: {
    accent: 'violet',
    badge: 'Advanced — Level 3',
    title: 'Go all-in with the Pro Trader Bundle',
    blurb: "You're ready for the full Hettie Nel methodology — swing trading, position trading, and the complete Pro Trader Bundle that includes every course she teaches. Lifetime access, lifetime updates.",
    courses: ['Swing Trading — $247', 'Position Trading — $247', 'Pro Trader Bundle — $897 (save $391)'],
    href: '/advanced',
    cta: 'View Advanced Courses',
  },
};

const ACCENT = {
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-400', bgSoft: 'bg-emerald-400/10', border: 'border-emerald-400/30', btn: 'bg-emerald-400 text-black hover:bg-emerald-300' },
  amber:   { text: 'text-amber-400',   bg: 'bg-amber-400',   bgSoft: 'bg-amber-400/10',   border: 'border-amber-400/30',   btn: 'bg-amber-400 text-black hover:bg-amber-300' },
  violet:  { text: 'text-violet-400',  bg: 'bg-violet-400',  bgSoft: 'bg-violet-400/10',  border: 'border-violet-400/30',  btn: 'bg-violet-400 text-black hover:bg-violet-300' },
};

function tally(answers) {
  const score = { beginner: 0, intermediate: 0, advanced: 0 };
  answers.forEach((a) => { if (a) score[a] += 1; });
  // Highest wins, ties resolve to lower level (safer for funnel)
  const order = ['advanced', 'intermediate', 'beginner'];
  let winner = 'beginner';
  let max = -1;
  order.reverse().forEach((tier) => {
    if (score[tier] >= max) { max = score[tier]; winner = tier; }
  });
  return { winner, score };
}

export default function CourseQuiz() {
  const [step, setStep] = useState(-1); // -1 = intro, 0..N-1 = questions, N = result
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const total = QUESTIONS.length;
  const isResult = step >= total;

  const start = () => setStep(0);
  const reset = () => { setAnswers(Array(total).fill(null)); setStep(-1); };

  const choose = (vote) => {
    const next = [...answers];
    next[step] = vote;
    setAnswers(next);
    setStep(step + 1);
  };

  const back = () => { if (step > 0) setStep(step - 1); };

  if (step === -1) {
    return (
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 sm:p-12 text-center" data-testid="quiz-intro">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-400 text-[11px] font-bold tracking-wider uppercase mb-5">
          <Sparkles size={13} /> Recommended for you
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Find your perfect course in 30 seconds</h2>
        <p className="mt-3 text-sm sm:text-base text-white/50 max-w-xl mx-auto leading-relaxed">
          Answer 5 quick questions and we'll match you with the level Hettie would personally recommend — based on her 20+ years training real traders.
        </p>
        <button
          onClick={start}
          className="mt-8 inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-amber-400 text-black font-bold text-sm hover:bg-amber-300 transition-all hover:scale-[1.02]"
          data-testid="quiz-start-btn"
        >
          Start the quiz <ArrowRight size={16} />
        </button>
        <p className="mt-4 text-[11px] text-white/30">No email required • Just for you</p>
      </div>
    );
  }

  if (isResult) {
    const { winner, score } = tally(answers);
    const r = RESULTS[winner];
    const a = ACCENT[r.accent];
    return (
      <div className={`rounded-2xl border ${a.border} bg-white/[0.02] p-8 sm:p-12`} data-testid="quiz-result">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${a.bgSoft} ${a.text} text-[11px] font-bold tracking-wider uppercase mb-5 border ${a.border}`}>
          <Sparkles size={13} /> Your match
        </div>
        <p className={`text-xs font-bold uppercase tracking-[0.2em] ${a.text} mb-2`}>{r.badge}</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{r.title}</h2>
        <p className="mt-4 text-sm sm:text-base text-white/60 leading-relaxed max-w-2xl">{r.blurb}</p>

        <div className="mt-6 pt-6 border-t border-white/[0.06]">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-3">We recommend</p>
          <ul className="space-y-2">
            {r.courses.map((c) => (
              <li key={c} className="flex items-center gap-2 text-sm text-white/85">
                <Check size={15} className={a.text} /> {c}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Link to={r.href} className={`inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full ${a.btn} font-bold text-sm transition-colors`} data-testid="quiz-result-cta">
            {r.cta} <ArrowRight size={15} />
          </Link>
          <button onClick={reset} className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full border border-white/15 text-white/70 text-sm font-semibold hover:border-white/30 hover:text-white transition-colors" data-testid="quiz-retake-btn">
            <RefreshCw size={14} /> Retake quiz
          </button>
        </div>

        <div className="mt-7 grid grid-cols-3 gap-2 max-w-md">
          {['beginner', 'intermediate', 'advanced'].map((tier) => {
            const tierAccent = ACCENT[RESULTS[tier].accent];
            const pct = Math.round((score[tier] / total) * 100);
            return (
              <div key={tier} className="text-center">
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className={`h-full ${tierAccent.bg} transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
                <p className="mt-1.5 text-[10px] uppercase tracking-wider text-white/40 capitalize">{tier} <span className={tierAccent.text}>{pct}%</span></p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Question state
  const current = QUESTIONS[step];
  const progress = Math.round(((step) / total) * 100);
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7 sm:p-10" data-testid={`quiz-question-${step}`}>
      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-7">
        <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
          <div className="h-full bg-amber-400 transition-all duration-500 ease-out" style={{ width: `${progress + (100/total)}%` }} />
        </div>
        <span className="text-[11px] font-mono text-white/40 tabular-nums">{step + 1}/{total}</span>
      </div>

      <div className="text-3xl mb-4" aria-hidden>{current.icon}</div>
      <h3 className="text-xl sm:text-2xl font-bold text-white leading-snug" data-testid="quiz-question-text">{current.q}</h3>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {current.options.map((opt, i) => (
          <button
            key={i}
            onClick={() => choose(opt.vote)}
            className="group text-left px-5 py-4 rounded-xl border border-white/[0.08] bg-white/[0.02] hover:border-amber-400/40 hover:bg-amber-400/[0.04] transition-all text-sm text-white/80 hover:text-white flex items-center justify-between gap-3"
            data-testid={`quiz-option-${step}-${i}`}
          >
            <span>{opt.label}</span>
            <ArrowRight size={15} className="text-white/20 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all" />
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={back}
          disabled={step === 0}
          className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="quiz-back-btn"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <button
          onClick={reset}
          className="text-xs text-white/30 hover:text-white/60 transition-colors"
          data-testid="quiz-restart-btn"
        >
          Restart
        </button>
      </div>
    </div>
  );
}
